/**
 * Coalesces assistant `token` frames into one dispatch per animation frame.
 *
 * The worker flushes tokens every ~50ms or 40 characters, and a busy socket
 * can deliver several of them between two paints. Dispatching each one
 * re-renders the transcript for text nobody sees, so tokens for the same
 * message are joined and dispatched once per frame. Any other frame kind
 * first flushes whatever tokens are pending (order is preserved: text that
 * arrived before `message_completed` is applied before it), then dispatches
 * straight away.
 *
 * Tokens carry a per-turn `seq` (backend A9) that the slice dedups on; a
 * merged frame carries only the highest seq it joined, so the slice can no
 * longer see a redelivered token inside it. The batcher therefore drops a
 * token whose `seq` is at or below the pending merged frame's own (when both
 * carry one) before joining its text. Frames without `seq` (a pre-A9
 * backend) merge as before.
 *
 * @param {(frame: object) => void} emit - receives each (possibly merged) frame
 * @param {{schedule?: (cb: () => void) => any, cancel?: (handle: any) => void}} [timing]
 *   injectable for tests; defaults to requestAnimationFrame, falling back to a 16ms timeout
 * @returns {{push: (frame: object) => void, flush: () => void, dispose: () => void}}
 */
/** `frame` appended to `pending`, carrying the highest seq either one has. */
function joinTokens(pending, frame) {
  const joined = { ...pending, text: (pending.text || '') + (frame.text || '') }
  if (frame.seq != null) joined.seq = pending.seq != null ? Math.max(pending.seq, frame.seq) : frame.seq
  return joined
}

export function createFrameBatcher(emit, timing = {}) {
  const hasRaf = typeof requestAnimationFrame === 'function'
  const schedule = timing.schedule || (hasRaf ? requestAnimationFrame : (cb) => setTimeout(cb, 16))
  const cancel = timing.cancel || (hasRaf ? cancelAnimationFrame : clearTimeout)
  let pending = null // a merged token frame waiting for the next frame
  let handle = null

  const flush = () => {
    if (handle != null) {
      cancel(handle)
      handle = null
    }
    if (!pending) return
    const frame = pending
    pending = null
    emit(frame)
  }

  const push = (frame) => {
    if (!frame || frame.kind !== 'token') {
      flush()
      emit(frame)
      return
    }
    const sameStream = pending && pending.message_id === frame.message_id && pending.session_id === frame.session_id
    if (pending && !sameStream) flush()
    if (sameStream && pending.seq != null && frame.seq != null && frame.seq <= pending.seq) return
    pending = sameStream ? joinTokens(pending, frame) : { ...frame }
    if (handle == null) {
      handle = schedule(() => {
        handle = null
        flush()
      })
    }
  }

  const dispose = () => {
    if (handle != null) cancel(handle)
    handle = null
    pending = null
  }

  return { push, flush, dispose }
}
